import { ReactNode, useMemo } from "react";
import { Link, useNavigate, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { boolAndReducer, getDatefromWeekNum, MyDate, Select, weeknum } from "src/components/common/functions";

type OffCalendarAssignment = {
    date?: string;
    hours?: number | string;
    startTime?: string;
    endTime?: string;
    requester?: string;
    status?: string;
    timeOffId: string;
    department?: string;
};

type CalendarView = "month" | "week" | "day";

type LoaderData = {
    weeks: string[];
    selectedWeek: string;
    activeDate: string;
    view: CalendarView;
    assignments: OffCalendarAssignment[];
};

type UserOption = {
    label: string;
    value: string;
};

const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayNamesShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const statusLegend = [
    { key: "Submitted", label: "Submitted", className: "calendarLegendChipSubmitted" },
    { key: "Approved", label: "Approved", className: "calendarLegendChipApproved" },
    { key: "Rejected", label: "Rejected", className: "calendarLegendChipRejected" },
] as const;

export default function OffCalendar() {
    const { weeks, selectedWeek, activeDate, view, assignments } = useRouteLoaderData("OffCalendar") as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const department = searchParams.get("department") ?? "All";
    const status = searchParams.get("status") ?? "All";
    const query = searchParams.get("q") ?? "";
    const selectedUsers = useMemo(
        () => (searchParams.get("users") ?? "").split(",").map(value => value.trim()).filter(Boolean),
        [searchParams]
    );

    const departments = useMemo(() => [
        "All",
        ...assignments
            .map(assignment => assignment.department)
            .filter(Boolean)
            .filter((value, index, array) => array.indexOf(value) === index)
            .sort(),
    ], [assignments]);

    const userOptions = useMemo<UserOption[]>(() => Array.from(
        new Set(assignments.map(assignment => assignment.requester).filter(Boolean))
    ).sort((a, b) => String(a).localeCompare(String(b))).map(requester => ({
        label: String(requester),
        value: String(requester),
    })), [assignments]);

    const selectedUserOptions = useMemo(
        () => userOptions.filter(option => selectedUsers.includes(option.value)),
        [selectedUsers, userOptions]
    );

    const filteredAssignments = useMemo(() => assignments.filter(assignment =>
        (status !== "All"
            ? (
                status === "Submitted"
                    ? assignment.status === "Submitted"
                    : status === "Approved"
                        ? assignment.status === "Approved"
                        : assignment.status === "Rejected"
            )
            : true
        ) &&
        assignment.hours !== 0 &&
        assignment.hours !== "0" &&
        (selectedUsers.length ? selectedUsers.includes(assignment.requester ?? "") : true) &&
        (department === "All" ? true : department === assignment.department) &&
        query.toLowerCase().split(" ").filter(Boolean).map(word => JSON.stringify(assignment.requester)?.toLowerCase().includes(word)).reduce(boolAndReducer, true)
    ), [assignments, status, selectedUsers, department, query]);

    const summary = {
        Submitted: filteredAssignments.filter(value => value.status === "Submitted").length,
        Approved: filteredAssignments.filter(value => value.status === "Approved").length,
        Rejected: filteredAssignments.filter(value => value.status === "Rejected").length,
    };

    const activeDateObject = useMemo(() => new MyDate(activeDate.replace(/-/g, "/")), [activeDate]);
    const weekDates = useMemo(() => Array.from({ length: 7 }, (_, index) => getDatefromWeekNum(selectedWeek, index) ?? ""), [selectedWeek]);
    const visibleDates = useMemo(() => (view === "day" ? [activeDate] : view === "week" ? weekDates : []), [activeDate, view, weekDates]);
    const monthGridDates = useMemo(() => buildMonthGrid(activeDateObject), [activeDateObject]);

    const assignmentsByDate = useMemo(() => {
        const grouped: Record<string, OffCalendarAssignment[]> = {};
        const allDates = view === "month" ? monthGridDates : visibleDates;
        allDates.forEach(date => {
            grouped[date] = filteredAssignments.filter(assignment => assignment.date === date);
        });
        return grouped;
    }, [filteredAssignments, monthGridDates, view, visibleDates]);

    const setParam = (key: string, value: string) => {
        const params = new URLSearchParams(searchParams);
        params.set(key, value);
        setSearchParams(params, { replace: false });
    };

    const navigateToState = (nextDate: string, nextView: CalendarView = view) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("date", nextDate);
        nextParams.set("view", nextView);
        navigate(`/OffCalendar/${weeknum(nextDate)}?${nextParams.toString()}`);
    };

    const navigateByOffset = (offset: number) => {
        const nextDate = shiftDate(activeDate, view === "month" ? { months: offset } : { days: view === "week" ? offset * 7 : offset });
        navigateToState(nextDate);
    };

    return (
        <div className = "calendarPage">
            <section className = "calendarToolbar">
                <div className = "calendarUserRow">
                    <Field label = "Users" htmlFor = "offCalendarUsers">
                        <Select
                            inputId = "offCalendarUsers"
                            classNamePrefix = "custom-select"
                            isMulti = {true}
                            isClearable = {true}
                            placeholder = "All users"
                            options = {userOptions}
                            value = {selectedUserOptions}
                            onChange = {(next: UserOption[] | null) => {
                                const nextParams = new URLSearchParams(searchParams);
                                nextParams.set("users", (next ?? []).map(option => option.value).join(","));
                                setSearchParams(nextParams);
                            }}
                        />
                    </Field>
                </div>
                <div className = "calendarToolbarGrid">
                    <Field label = "Search" htmlFor = "offCalendarSearch">
                        <input
                            id = "offCalendarSearch"
                            className = "form-control"
                            type = "text"
                            value = {query}
                            onChange = {e => setParam("q", e.currentTarget.value.toLowerCase())}
                        />
                    </Field>
                    <Field label = "Status" htmlFor = "offCalendarStatus">
                        <select
                            id = "offCalendarStatus"
                            className = "form-control"
                            value = {status}
                            onChange = {e => setParam("status", e.currentTarget.value)}
                        >
                            <option>All</option>
                            <option>Submitted</option>
                            <option>Approved</option>
                            <option>Rejected</option>
                        </select>
                    </Field>
                    <Field label = "Department" htmlFor = "offCalendarDepartment">
                        <select
                            id = "offCalendarDepartment"
                            className = "form-control"
                            value = {department}
                            onChange = {e => setParam("department", e.currentTarget.value)}
                        >
                            {departments.map((dpt, key) => <option key = {key}>{dpt}</option>)}
                        </select>
                    </Field>
                </div>
            </section>
            <section className = "calendarLegend">
                {statusLegend.map(item => (
                    <div className = {`calendarLegendChip ${item.className}`} key = {item.key}>
                        <span>{item.label}</span>
                        <strong>{summary[item.key]}</strong>
                    </div>
                ))}
            </section>
            <div className = "calendarNavInputs">
                <div className = "calendarViewSwitch" role = "tablist" aria-label = "Off calendar views">
                    {(["month", "week", "day"] as CalendarView[]).map(option => (
                        <button
                            key = {option}
                            className = {`calendarViewButton${view === option ? " active" : ""}`}
                            type = "button"
                            onClick = {() => navigateToState(activeDate, option)}
                        >
                            {capitalize(option)}
                        </button>
                    ))}
                </div>
                {view === "month" ? (
                    <Field htmlFor = "offCalendarMonth">
                        <select
                            id = "offCalendarMonth"
                            className = "form-control"
                            value = {activeDate.slice(0, 7)}
                            onChange = {e => navigateToState(`${e.currentTarget.value}-01`)}
                        >
                            {getMonthOptions(weeks).map((value, index) => (
                                <option key = {index} value = {value}>{formatMonthOption(value)}</option>
                            ))}
                        </select>
                    </Field>
                ) : null}
                {view === "week" ? (
                    <Field htmlFor = "offCalendarWeek">
                        <select
                            id = "offCalendarWeek"
                            className = "form-control"
                            value = {selectedWeek}
                            onChange = {e => navigateToState(getDatefromWeekNum(e.currentTarget.value, 0) ?? activeDate)}
                        >
                            {weeks.map((wk, key) => <option key = {key}>{wk}</option>)}
                        </select>
                    </Field>
                ) : null}
                {view === "day" ? (
                    <Field htmlFor = "offCalendarDate">
                        <input
                            id = "offCalendarDate"
                            className = "form-control"
                            type = "date"
                            value = {activeDate}
                            onChange = {e => navigateToState(e.currentTarget.value)}
                        />
                    </Field>
                ) : null}
                <div className = "calendarNavButtons">
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(-1)}>Prev</button>
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(1)}>Next</button>
                </div>
            </div>
            {view === "month" ? (
                        <section className = "offCalendarMonthGrid">
                            <div className = "calendarMonthHeader">
                                {dayNamesShort.map(day => <div className = "calendarMonthHeaderCell" key = {day}>{day}</div>)}
                            </div>
                            <div className = "calendarMonthGrid">
                                {monthGridDates.map(date => {
                                    const items = assignmentsByDate[date] ?? [];
                                    const inMonth = date.slice(0, 7) === activeDate.slice(0, 7);
                                    return (
                                        <div className = {`calendarMonthCell${inMonth ? "" : " outside"}${date === activeDate ? " active" : ""}`} key = {date}>
                                            <button className = "calendarMonthDateButton" type = "button" onClick = {() => navigateToState(date, "day")}>
                                                <div className = "calendarMonthDateRow">
                                                    <span>{date.slice(8, 10)}</span>
                                                    <strong>{items.length || ""}</strong>
                                                </div>
                                            </button>
                                            <div className = "calendarMonthAssignments">
                                                {items.slice(0, 3).map(item => (
                                                    <Link className = "calendarMonthAssignment calendarMonthAssignmentLink" to = {`/TimeOffs/${item.timeOffId}`} key = {`${date}-${item.timeOffId}`}>
                                                        <span className = "calendarMonthAssignmentDot" style = {{ backgroundColor: getOffStatusColor(item.status) }} />
                                                        <span className = "calendarMonthAssignmentText">{item.requester}</span>
                                                    </Link>
                                                ))}
                                                {items.length > 3 ? <div className = "calendarMonthMore">+{items.length - 3} more</div> : null}
                                                {!items.length ? <div className = "calendarMonthEmpty">No time off</div> : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ) : (
                        <section className = {`offCalendarWeekGrid${view === "day" ? " offCalendarSingleGrid" : ""}`}>
                            {visibleDates.map((date, index) => {
                                const dayAssignments = assignmentsByDate[date] ?? [];
                                return (
                                    <div className = "offCalendarDayColumn" key = {date}>
                                        {view === "week" ? (
                                            <button className = "calendarTimedDayHeader" type = "button" onClick = {() => navigateToState(date, "day")}>
                                                <span>{dayNames[index]}</span>
                                                <strong>{date}</strong>
                                            </button>
                                        ) : (
                                            <div className = "offCalendarDayHeader">
                                                <div className = "offCalendarDayName">{formatLongDayName(date)}</div>
                                                <div className = "offCalendarDayDate">{date}</div>
                                            </div>
                                        )}
                                        <div className = "offCalendarDayBody">
                                            {dayAssignments.length ? dayAssignments.map((assignment, assignmentIndex) => (
                                                <Link className = "offCalendarEventCard" key = {assignmentIndex} to = {`/TimeOffs/${assignment.timeOffId}`}>
                                                    <div className = "offCalendarEventHeader">
                                                        <div className = "offCalendarEventRequester">{assignment.requester}</div>
                                                        <span className = {`offCalendarStatusBadge ${getOffStatusClassName(assignment.status)}`}>
                                                            {assignment.status}
                                                        </span>
                                                    </div>
                                                    <div className = "offCalendarEventMeta">
                                                        {assignment.hours === "8" || assignment.hours === 8
                                                            ? "Full day"
                                                            : `${assignment.startTime ?? ""} ~ ${assignment.endTime ?? ""}`}
                                                    </div>
                                                    {assignment.department ? (
                                                        <div className = "offCalendarEventMeta">{assignment.department}</div>
                                                    ) : null}
                                                </Link>
                                            )) : (
                                                <div className = "offCalendarEmptyDay">No time off</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </section>
                    )}
        </div>
    );
}

function Field({ label, htmlFor, children }: { label?: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarField" htmlFor = {htmlFor}>
            {label?<span className = "calendarFieldLabel">{label}</span>:null}
            {children}
        </label>
    );
}

function buildMonthGrid(activeDate: Date) {
    const firstOfMonth = new MyDate(activeDate.getFullYear(), activeDate.getMonth(), 1);
    const firstDayIndex = getMondayOffset(firstOfMonth.s());
    const gridStart = new MyDate(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstDayIndex);
    return Array.from({ length: 42 }, (_, index) => {
        const date = new MyDate(gridStart);
        date.setDate(gridStart.getDate() + index);
        return date.s();
    });
}

function getMondayOffset(dateString: string) {
    const weekday = new Date(dateString.replace(/-/g, "/")).getDay();
    return weekday === 0 ? 6 : weekday - 1;
}

function shiftDate(dateString: string, offset: { days?: number; months?: number }) {
    const date = new MyDate(dateString.replace(/-/g, "/"));
    if (offset.months) date.setMonth(date.getMonth() + offset.months);
    if (offset.days) date.setDate(date.getDate() + offset.days);
    return date.s();
}

function getMonthOptions(weeks: string[]) {
    return Array.from(new Set(weeks
        .map(week => getDatefromWeekNum(week, 0)?.slice(0, 7))
        .filter((value): value is string => Boolean(value))
    ));
}

function formatMonthOption(value: string) {
    return new Date(`${value}-01`.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });
}

function formatLongDayName(date: string) {
    return new Date(date.replace(/-/g, "/")).toLocaleDateString("en-US", { weekday: "long" });
}

function capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function getOffStatusClassName(status?: string) {
    if (status === "Submitted") return "calendarLegendChipSubmitted";
    if (status === "Approved") return "calendarLegendChipApproved";
    return "calendarLegendChipRejected";
}

function getOffStatusColor(status?: string) {
    if (status === "Submitted") return "purple";
    if (status === "Approved") return "green";
    return "gray";
}
